import { Injectable, inject } from '@angular/core';
import {
  injectMutation,
  injectQuery,
  injectQueryClient,
} from '@tanstack/angular-query-experimental';
import { firstValueFrom, map } from 'rxjs';
import { Client, RegisterProfile, UpdateProfile } from '../../api/api-clients';
import { ProfileConverter } from '../converters/profile.converter';
import type { AppProfile } from '../models/profile.model';
import { profileKeys } from './query-keys';

@Injectable({ providedIn: 'root' })
export class ProfileService {
  private readonly client = inject(Client);
  private readonly queryClient = injectQueryClient();

  readonly myProfile = injectQuery(() => ({
    queryKey: profileKeys.me(),
    queryFn: () =>
      firstValueFrom(
        this.client.profilesGET().pipe(map((p) => ProfileConverter.toAppModel(p))),
      ),
  }));

  readonly myViews = injectQuery(() => ({
    queryKey: profileKeys.views(),
    queryFn: () => firstValueFrom(this.client.views()),
  }));

  readonly createProfile = injectMutation(() => ({
    mutationFn: (body: RegisterProfile) => firstValueFrom(this.client.profilesPOST(body)),
    onSuccess: (newProfile) => {
      this.queryClient.setQueryData(profileKeys.me(), ProfileConverter.toAppModel(newProfile));
    },
  }));

  readonly updateProfile = injectMutation(() => ({
    mutationFn: (body: UpdateProfile) => firstValueFrom(this.client.profilesPUT(body)),
    onMutate: async (variables) => {
      await this.queryClient.cancelQueries({ queryKey: profileKeys.me() });
      const previous = this.queryClient.getQueryData<AppProfile>(profileKeys.me());
      if (previous) {
        this.queryClient.setQueryData(profileKeys.me(), applyUpdateToProfile(previous, variables));
      }
      return { previous };
    },
    onError: (_err, _variables, context) => {
      if (context?.previous) {
        this.queryClient.setQueryData(profileKeys.me(), context.previous);
      }
    },
    onSettled: () => {
      this.queryClient.invalidateQueries({ queryKey: profileKeys.me() });
    },
  }));
}

function applyUpdateToProfile(profile: AppProfile, update: UpdateProfile): AppProfile {
  const base: AppProfile = {
    ...profile,
    profilePicture: update.profilePicture ?? profile.profilePicture,
    city: update.city ?? profile.city,
    country: update.country ?? profile.country,
    description: update.description ?? profile.description,
    additionalInformation: update.additionalInformation ?? profile.additionalInformation,
    videos: update.videos ?? profile.videos,
    location: update.location ?? profile.location,
  };

  if (update.profileData) {
    try {
      const typeData = JSON.parse(update.profileData);
      return { ...base, ...typeData } as AppProfile;
    } catch {
      return base;
    }
  }

  return base;
}
